import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { auth, firestore, functions, signOutIdentityIfActive } from '../firebase';
import { resolveVolunteerPageEventId } from '../utils/activeEvent';
import { fetchSiteRegistrationSettings } from '../utils/siteSettings';
import {
  distinctSortedDates,
  sortShiftsForDisplay,
  isShiftFull,
  findOverlappingShiftPairs,
  formatVolunteerCompactDay,
  formatVolunteerTabParts,
  getVolunteerTimePillDisplay,
  minuteSortKeyForShift,
} from '../utils/volunteerShifts';
import { ShiftTaskCard, volunteerTimeLineLabel } from './VolunteerShiftTaskCard';
import './VolunteerSignup.css';

const checkVolunteerRegistrationFn = httpsCallable(functions, 'checkVolunteerRegistration');

const DEFAULT_FURY_REGISTRATION_URL = 'https://fury-registration.web.app';

function callableErrorMessage(err) {
  const code = `${err?.code || ''} ${err?.message || ''}`;
  const msg = err?.message || '';
  if (code.includes('already-exists')) return 'You already signed up for this shift.';
  if (code.includes('failed-precondition')) return msg.includes('full') ? 'This shift is full.' : msg || 'Could not update this shift.';
  if (code.includes('not-found')) return 'Shift not found.';
  if (code.includes('unauthenticated')) return 'Please sign in again.';
  return msg || 'Something went wrong. Try again.';
}

function VolunteerShellHeader({ user, loggingOut, onLogout }) {
  const label = user ? (user.displayName || user.email || 'Signed in').trim() : '';
  return (
    <header className="volunteer-page__header">
      <div className="volunteer-page__header-top">
        <div className="volunteer-page__header-titles">
          <div className="volunteer-page__header-kicker">Fresh Meet 2027</div>
          <div className="volunteer-page__header-title">Volunteer sign-up</div>
        </div>
        {user ? (
          <button
            type="button"
            className="volunteer-page__header-logout"
            disabled={loggingOut}
            title={label}
            onClick={onLogout}
          >
            {loggingOut ? '…' : 'Log out'}
          </button>
        ) : null}
      </div>
    </header>
  );
}

function VolunteerFooter() {
  return (
    <footer className="volunteer-page__footer">
      A Fury Coaching event · Skydive Perris
    </footer>
  );
}

export default function VolunteerSignup() {
  const [searchParams] = useSearchParams();
  const [user] = useAuthState(auth);
  const [eventId, setEventId] = useState('');
  const [eventMeta, setEventMeta] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [myShiftsOpen, setMyShiftsOpen] = useState(true);
  const [overlapWarnDismissed, setOverlapWarnDismissed] = useState(false);
  /** null = still checking (when eventId set); true = may use shifts; false = show Fury Registration CTA */
  const [volunteerAllowed, setVolunteerAllowed] = useState(null);
  const [volunteerBlockReason, setVolunteerBlockReason] = useState(null);
  const [volunteerGateDebug, setVolunteerGateDebug] = useState(null);
  const [registrationUrl, setRegistrationUrl] = useState(DEFAULT_FURY_REGISTRATION_URL);

  const handleLogout = async () => {
    setLoggingOut(true);
    setError('');
    try {
      await auth.signOut();
      await signOutIdentityIfActive();
    } catch (e) {
      setError(e.message || 'Sign out failed.');
    } finally {
      setLoggingOut(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = await resolveVolunteerPageEventId(searchParams.get('event'));
        if (cancelled) return;
        setEventId(id);
        if (id) {
          const evSnap = await getDoc(doc(firestore, 'events', id));
          setEventMeta(evSnap.exists() ? { id: evSnap.id, ...evSnap.data() } : null);
        } else {
          setEventMeta(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not resolve event.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  useEffect(() => {
    if (loading) return undefined;
    if (!eventId) {
      setVolunteerAllowed(true);
      setVolunteerBlockReason(null);
      setVolunteerGateDebug(null);
      return undefined;
    }
    let cancelled = false;
    setVolunteerAllowed(null);
    setVolunteerBlockReason(null);
    (async () => {
      try {
        const { registrationUrl: regUrl } = await fetchSiteRegistrationSettings();
        if (!cancelled && regUrl) setRegistrationUrl(regUrl);
      } catch {
        /* keep default */
      }
      try {
        if (user?.reload) {
          await user.reload();
        }
      } catch {
        /* ignore */
      }
      if (cancelled) return;
      try {
        const result = await checkVolunteerRegistrationFn({eventId});
        if (cancelled) return;
        const {matched, matchedBy, matchedFlyerId, flyerDocs, blockReason} = result.data;
        if (process.env.NODE_ENV !== 'production') {
          console.info('[VolunteerGate] registration match', {
            eventId, uid: user?.uid || null, email: user?.email || null,
            matched, matchedBy, matchedFlyerId, flyerDocs, blockReason,
          });
        }
        setVolunteerAllowed(matched);
        setVolunteerBlockReason(blockReason);
        setVolunteerGateDebug({
          eventId, uid: user?.uid || null, email: user?.email || null,
          providerEmails: Array.isArray(user?.providerData)
            ? user.providerData.map((p) => p?.email).filter(Boolean) : [],
          flyerDocs, matched, matchedBy, matchedFlyerId: matchedFlyerId || null, blockReason,
        });
      } catch (e) {
        if (!cancelled) {
          setVolunteerAllowed(true);
          setVolunteerGateDebug({eventId, uid: user?.uid || null, email: user?.email || null, error: e?.message || String(e)});
          console.warn('Volunteer roster check failed; allowing shift UI.', e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, eventId, user]);

  useEffect(() => {
    if (!eventId) {
      setShifts([]);
      setSelectedDate('');
      return undefined;
    }
    const q = query(collection(firestore, 'volunteerShifts'), where('event', '==', eventId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setShifts(rows);
        const dateList = distinctSortedDates(rows);
        setSelectedDate((prev) => {
          if (prev && dateList.includes(prev)) return prev;
          return dateList[0] || '';
        });
      },
      (e) => setError(e.message || 'Failed to load shifts.'),
    );
    return () => unsub();
  }, [eventId]);

  const dates = useMemo(() => distinctSortedDates(shifts), [shifts]);
  const shiftsForDay = useMemo(() => shifts.filter((s) => (s.date || '') === selectedDate), [shifts, selectedDate]);
  const dominantDayType = useMemo(() => {
    if (!shiftsForDay.length) return 'shoulder';
    if (shiftsForDay.some((s) => s.dayType === 'event')) return 'event';
    return 'shoulder';
  }, [shiftsForDay]);
  const displayShifts = useMemo(() => {
    if (dominantDayType === 'event') return shiftsForDay.filter((s) => s.dayType === 'event');
    return shiftsForDay;
  }, [shiftsForDay, dominantDayType]);
  const sortedDay = useMemo(() => sortShiftsForDisplay(displayShifts), [displayShifts]);

  const myShifts = useMemo(() => {
    if (!user) return [];
    return shifts.filter((s) => (s.assignees || []).some((a) => a && a.uid === user.uid));
  }, [shifts, user]);

  const myShiftsByDay = useMemo(() => {
    if (!myShifts.length) return [];
    const byDay = new Map();
    for (const s of myShifts) {
      const d = (s.date || '').trim();
      if (!byDay.has(d)) byDay.set(d, []);
      byDay.get(d).push(s);
    }
    const daysSorted = [...byDay.keys()].sort();
    return daysSorted.map((date) => ({
      date,
      shifts: sortShiftsForDisplay(byDay.get(date)),
    }));
  }, [myShifts]);

  const myCountByDate = useMemo(() => {
    const m = new Map();
    for (const s of myShifts) {
      const d = (s.date || '').trim();
      if (!d) continue;
      m.set(d, (m.get(d) || 0) + 1);
    }
    return m;
  }, [myShifts]);

  const overlapPairs = useMemo(() => findOverlappingShiftPairs(myShifts), [myShifts]);

  useEffect(() => {
    if (overlapPairs.length === 0) setOverlapWarnDismissed(false);
  }, [overlapPairs.length]);

  const shiftsByTimeBlock = useMemo(() => {
    if (!sortedDay.length) return [];
    const map = new Map();
    for (const s of sortedDay) {
      const tb = (s.timeBlock || '').toString().trim() || '—';
      if (!map.has(tb)) map.set(tb, []);
      map.get(tb).push(s);
    }
    return [...map.entries()]
      .sort((a, b) => {
        const da = minuteSortKeyForShift(a[1][0]);
        const db = minuteSortKeyForShift(b[1][0]);
        if (da !== db) return da - db;
        return (a[0] || '').localeCompare(b[0] || '');
      })
      .map(([timeBlock, blockShifts]) => {
        const sorted = sortShiftsForDisplay(blockShifts);
        const openSpots = sorted.reduce((acc, s) => {
          const need = Number(s.needed) || 0;
          const claimed = (s.assignees || []).length;
          return acc + Math.max(0, need - claimed);
        }, 0);
        return { timeBlock, shifts: sorted, openSpots };
      });
  }, [sortedDay]);

  const claim = async (shift) => {
    if (!user || !shift?.id) return;
    setBusyId(shift.id);
    setError('');
    try {
      const fn = httpsCallable(functions, 'volunteerShiftClaim');
      const displayName =
        (user.displayName && user.displayName.trim()) ||
        (user.email && user.email.trim()) ||
        (user.phoneNumber && user.phoneNumber.trim()) ||
        '';
      await fn({
        shiftId: shift.id,
        displayName,
      });
    } catch (e) {
      setError(callableErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const release = async (shift) => {
    if (!user || !shift?.id) return;
    setBusyId(shift.id);
    setError('');
    try {
      const fn = httpsCallable(functions, 'volunteerShiftRelease');
      await fn({ shiftId: shift.id });
    } catch (e) {
      setError(callableErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const volunteerChecking = Boolean(eventId && volunteerAllowed === null);

  const innerLoading = loading || volunteerChecking;

  if (innerLoading) {
    return (
      <div className="volunteer-page">
        <div className="volunteer-page__column">
          <VolunteerShellHeader user={user} loggingOut={loggingOut} onLogout={handleLogout} />
          <div className="volunteer-page__orange-rule" aria-hidden />
          <div className="volunteer-page__loading">Loading…</div>
          <VolunteerFooter />
        </div>
      </div>
    );
  }

  if (!eventId) {
    return (
      <div className="volunteer-page">
        <div className="volunteer-page__column">
          <VolunteerShellHeader user={user} loggingOut={loggingOut} onLogout={handleLogout} />
          <div className="volunteer-page__orange-rule" aria-hidden />
          {error && <p className="volunteer-page__error">{error}</p>}
          <p className="volunteer-page__no-event">No event is configured yet. Check back later.</p>
          <VolunteerFooter />
        </div>
      </div>
    );
  }

  const myCount = myShifts.length;
  const myBadgeText = myCount === 0 ? 'None yet' : `${myCount} ${myCount === 1 ? 'shift' : 'shifts'}`;

  return (
    <div className="volunteer-page">
      <div className="volunteer-page__column">
        <VolunteerShellHeader user={user} loggingOut={loggingOut} onLogout={handleLogout} />
        <div className="volunteer-page__orange-rule" aria-hidden />

        {error && <p className="volunteer-page__error">{error}</p>}

        {volunteerAllowed === false && (
          <section className="volunteer-page__registration-gate">
            <h2 className="volunteer-page__registration-gate-title">Registration could not be verified</h2>
            {volunteerBlockReason === 'no_email' ? (
              <p className="volunteer-page__registration-gate-lede">
                This login does not have an email address, so we cannot match it to an event registration. You can still choose shifts now, and if
                needed complete registration with an email at Fury Registration.
              </p>
            ) : (
              <p className="volunteer-page__registration-gate-lede">
                We did not find a freshling, mentor, or volunteer registration for <strong>{eventMeta?.title || 'this event'}</strong> matching this
                login email. You can still use shift signup now, or register/update details in Fury Registration.
              </p>
            )}
            <p className="volunteer-page__registration-gate-actions">
              <a
                className="volunteer-page__registration-gate-link"
                href={registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Fury Registration
              </a>
            </p>
            {volunteerGateDebug ? (
              <details className="volunteer-page__gate-debug">
                <summary>Verification debug</summary>
                <pre>{JSON.stringify(volunteerGateDebug, null, 2)}</pre>
              </details>
            ) : null}
          </section>
        )}

        <section className="volunteer-page__my-shifts">
          <button
            type="button"
            className="volunteer-page__my-shifts-toggle"
            aria-expanded={myShiftsOpen}
            onClick={() => setMyShiftsOpen((o) => !o)}
          >
            <span className="volunteer-page__my-shifts-label">My shifts</span>
            <span className="volunteer-page__my-shifts-right">
              <span className={`volunteer-page__count-badge${myCount === 0 ? ' volunteer-page__count-badge--empty' : ''}`}>{myBadgeText}</span>
              <span className={`volunteer-page__chevron${myShiftsOpen ? '' : ' volunteer-page__chevron--collapsed'}`} aria-hidden="true">
                ▼
              </span>
            </span>
          </button>
          {myShiftsOpen && (
            <div className="volunteer-page__my-shifts-body">
              {myCount === 0 ? (
                <p className="volunteer-page__my-empty">No shifts yet — pick one below!</p>
              ) : (
                <>
                  {overlapPairs.length > 0 && !overlapWarnDismissed && (
                    <div className="volunteer-page__warn" role="status">
                      <button
                        type="button"
                        className="volunteer-page__warn-dismiss"
                        aria-label="Dismiss overlap notice"
                        onClick={() => setOverlapWarnDismissed(true)}
                      >
                        ×
                      </button>
                      Some of your shifts overlap in time — double-check that you are okay covering both.
                    </div>
                  )}
                  {myShiftsByDay.map((day) => (
                    <div key={day.date || 'unknown'} className="volunteer-page__my-day">
                      <div className="volunteer-page__my-day-label">{formatVolunteerCompactDay(day.date)}</div>
                      {day.shifts.map((s) => (
                        <div key={s.id} className="volunteer-page__my-mini">
                          <div className="volunteer-page__my-mini-left">
                            <div className="volunteer-page__my-mini-role">{(s.role || '').toString()}</div>
                            <div className="volunteer-page__my-mini-time">{volunteerTimeLineLabel(s)}</div>
                          </div>
                          <button type="button" className="volunteer-page__release" disabled={busyId === s.id} onClick={() => release(s)}>
                            Release
                          </button>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </section>

        <div className="volunteer-page__day-tabs" role="tablist" aria-label="Event day">
          {dates.map((d) => {
            const { dayAbbr, dateLine } = formatVolunteerTabParts(d);
            const tabCount = myCountByDate.get(d) || 0;
            return (
              <button
                key={d}
                type="button"
                role="tab"
                aria-selected={d === selectedDate}
                className={`volunteer-page__day-tab${d === selectedDate ? ' volunteer-page__day-tab--active' : ''}`}
                onClick={() => setSelectedDate(d)}
              >
                <span className="volunteer-page__day-tab-inner">
                  {tabCount > 0 ? (
                    <span className="volunteer-page__day-tab-badge" aria-label={`${tabCount} signed up`}>
                      {tabCount > 9 ? '9+' : tabCount}
                    </span>
                  ) : null}
                  <span className="volunteer-page__day-tab-day">{dayAbbr}</span>
                  <span className="volunteer-page__day-tab-date">{dateLine}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="volunteer-page__shift-list">
          {!selectedDate && <p className="volunteer-page__list-empty">No shifts published for this event yet.</p>}

          {selectedDate && shiftsByTimeBlock.length === 0 && (
            <p className="volunteer-page__list-empty">No shifts this day.</p>
          )}

          {selectedDate &&
            shiftsByTimeBlock.map(({ timeBlock, shifts: blockShifts, openSpots }) => {
              const pill = blockShifts[0] ? getVolunteerTimePillDisplay(blockShifts[0]) : { variant: 'fallback', text: timeBlock };
              const pillClass = [
                'volunteer-page__time-pill',
                pill.variant === 'allday' ? 'volunteer-page__time-pill--allday' : '',
                pill.variant === 'fallback' ? 'volunteer-page__time-pill--fallback' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const pillText = pill.variant === 'allday' ? 'All day' : pill.text;
              return (
                <section key={timeBlock} className="volunteer-page__time-block">
                  <div className="volunteer-page__time-block-head">
                    <span className={pillClass}>{pillText}</span>
                    <span className="volunteer-page__time-rule" aria-hidden />
                    <span className={`volunteer-page__time-count${openSpots > 0 ? ' volunteer-page__time-count--open' : ''}`}>
                      {openSpots > 0 ? `${openSpots} open` : 'all full'}
                    </span>
                  </div>
                  <div className="volunteer-page__time-block-cards">
                    {blockShifts.map((s) => {
                      const claimed = (s.assignees || []).length;
                      const need = Number(s.needed) || 0;
                      const full = isShiftFull(s);
                      const mine = (s.assignees || []).some((a) => a && a.uid === user?.uid);
                      const open = Math.max(0, need - claimed);
                      return (
                        <ShiftTaskCard
                          key={s.id}
                          shift={s}
                          mine={mine}
                          full={full}
                          openSpots={open}
                          busy={busyId === s.id}
                          onClaim={claim}
                          onRelease={release}
                        />
                      );
                    })}
                  </div>
                </section>
              );
            })}
        </div>

        <VolunteerFooter />
      </div>
    </div>
  );
}
